/**
 * 用户中心各页共用的展示组件。
 * 配色约定：强调色只用于交互与关键数字；收益只在数字上着色，不铺彩色底色。
 */

import type { ReactNode } from 'react';
import {
  ArrowDownLeft,
  ImageIcon,
  RotateCcw,
  Sparkles,
  User,
  Users,
} from 'lucide-react';
import type { CreditTransactionItem } from '../../services/api';
import { formatInBeijing } from '../../utils/time';

export function SectionCard({
  title,
  subtitle,
  action,
  children,
  bodyClassName = 'p-0',
}: {
  title?: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
  bodyClassName?: string;
}) {
  return (
    <section className="bg-white border border-light-border rounded-2xl shadow-sm overflow-hidden">
      {(title || action) && (
        <div className="px-5 py-4 border-b border-light-border flex items-center justify-between gap-3">
          <div>
            {title && <h2 className="text-sm font-semibold text-light-text">{title}</h2>}
            {subtitle && <p className="text-xs text-light-muted mt-0.5">{subtitle}</p>}
          </div>
          {action}
        </div>
      )}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

export function Metric({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: ReactNode;
  tone?: 'earn' | 'spend' | 'neutral';
}) {
  const toneClass =
    tone === 'earn' ? 'text-green-600' : tone === 'spend' ? 'text-red-500' : 'text-light-text';
  return (
    <div>
      <p className="text-xs text-light-muted">{label}</p>
      <p className={`text-lg font-semibold mt-1 tabular-nums ${toneClass}`}>{value}</p>
    </div>
  );
}

export function TransactionTypeIcon({ type }: { type: string }) {
  if (type === 'spend') {
    return <ImageIcon className="w-4 h-4 text-light-secondary" />;
  }
  if (type === 'refund' || type === 'referral_rebate_reverse') {
    return <RotateCcw className="w-4 h-4 text-light-secondary" />;
  }
  if (type === 'referral_rebate') {
    return <Users className="w-4 h-4 text-light-secondary" />;
  }
  if (type === 'recharge' || type === 'earn') {
    return <ArrowDownLeft className="w-4 h-4 text-light-secondary" />;
  }
  return <Sparkles className="w-4 h-4 text-light-secondary" />;
}

const TYPE_LABELS: Record<string, string> = {
  spend: '消费',
  earn: '获得',
  recharge: '充值',
  refund: '退款',
  referral_rebate: '推广返利',
  referral_rebate_reverse: '返利冲销',
};

export function transactionTypeLabel(type: string): string {
  return TYPE_LABELS[type] ?? '变动';
}

export function formatTransactionTime(value: string): string {
  return formatInBeijing(value, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function TransactionRow({ tx }: { tx: CreditTransactionItem }) {
  const isPositive = tx.amount > 0;
  return (
    <div className="px-5 py-3.5 flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-light-hover flex items-center justify-center shrink-0">
        <TransactionTypeIcon type={tx.type} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-light-text truncate">
          {tx.description || transactionTypeLabel(tx.type)}
        </p>
        <p className="text-xs text-light-muted mt-0.5">
          {transactionTypeLabel(tx.type)} · {formatTransactionTime(tx.createdAt)}
        </p>
      </div>
      <span
        className={`text-sm font-semibold tabular-nums shrink-0 ${
          isPositive ? 'text-green-600' : 'text-red-500'
        }`}
      >
        {isPositive ? '+' : ''}
        {tx.amount}
      </span>
    </div>
  );
}

export function EmptyState({ text }: { text: string }) {
  return <div className="py-10 text-center text-sm text-light-muted">{text}</div>;
}

export function UserAvatar({ name }: { name?: string | null }) {
  const initial = (name || '').trim().charAt(0).toUpperCase();
  return (
    <div className="w-12 h-12 rounded-2xl bg-accent-blue/10 flex items-center justify-center shrink-0">
      {initial ? (
        <span className="text-lg font-semibold text-accent-blue">{initial}</span>
      ) : (
        <User className="w-6 h-6 text-accent-blue" />
      )}
    </div>
  );
}
